import queryString, { stringify } from 'query-string';
import { ITaskType, IWorkflow } from '../Jobs/types';
import RecruitApiClient from '../../RecruitApiClient';
import { IEmailTemplateList } from '../Application/types';
import { IJobTemplate } from '../Templates/types';

const BASE_URL = window.location.origin;
class Api {
  getHeaders(headers: {}): {} {
    return Object.assign(
      {},
      {
        'Content-Type': 'application/json'
      },
      headers
    );
  }

  handleResponse = (response) => {
    if (response.ok) return response.json();
    return response.json().then((error) => {
      error.code = response.status;
      throw error;
    });
  };

  handlePaginatedResponse = async (response) => {
    if (response.ok) {
      return {
        data: await response.json(),
        totalItems: Number(response.headers.get('X-Total-Count')),
        totalPages: Number(response.headers.get('X-Total-Pages'))
      };
    }
    return response.json().then((error) => {
      error.code = response.status;
      throw error;
    });
  };

  getPaginated = (path: string, params = {}, headers?: { 'X-api-authenticate': string }) =>
    fetch(
      `${BASE_URL}${path}?${queryString.stringify(params, {
        skipEmptyString: true,
        skipNull: true,
        arrayFormat: 'bracket',
        encode: false
      })}`,
      {
        headers: headers ? this.getHeaders(headers) : {}
      }
    ).then((r) => this.handlePaginatedResponse(r));

  get = (path: string, params = {}, headers?: { 'X-api-authenticate': string }) =>
    fetch(
      `${BASE_URL}${path}?${queryString.stringify(params, {
        arrayFormat: 'bracket',
        encode: false
      })}`,
      {
        headers: headers ? this.getHeaders(headers) : {}
      }
    ).then((r) => this.handleResponse(r));

  post = (path: string, params = {}, headers = {}) =>
    fetch(`${BASE_URL}${path}`, {
      headers: this.getHeaders(headers),
      method: 'POST',
      body: JSON.stringify(params)
    }).then((r) => this.handleResponse(r));

  put = (path: string, params = {}, headers?: { 'X-api-authenticate': string }) =>
    fetch(`${BASE_URL}${path}`, {
      headers: this.getHeaders(headers ?? {}),
      method: 'PUT',
      body: JSON.stringify(params)
    }).then((r) => this.handleResponse(r));

  delete = (path: string, params = {}, headers?: { 'X-api-authenticate': string }) =>
    fetch(`${BASE_URL}${path}`, {
      headers: this.getHeaders(headers ?? {}),
      method: 'DELETE',
      body: JSON.stringify(params)
    }).then((r) => this.handleResponse(r));

  getJob = (jobId: number) => this.get(`/api/v4/jobs/${jobId}`, {});
  getJobTemplate = (jobTemplateId: number) =>
    this.get(`/api/v4/job_templates/${jobTemplateId}`, {});
  getJobs = (page: number) => this.getPaginated(`/api/v4/tasks/jobs`, { page });
  getApplicationsForTask = (jobId: number, page: number) =>
    this.getPaginated(`/api/v4/tasks/jobs/${jobId}/applications`, { page });
  getActiveSeekAd = (jobId: number) => this.get(`/api/v4/seek/jobs/${jobId}/job_ads`, {});
  getJobCounts = (jobId: number) => this.get(`/api/v4/jobs/${jobId}/counters`, {});
  putChangedState = (link: string) => this.put(link, {});
  putChangedStatus = (link: string) => this.put(link, {});
  getGraphData = (url: string, props: { start_date: string; end_date: string }) =>
    this.get(url, props);
  getJobAttachments = (jobId: number, params) =>
    this.getPaginated(`/api/v4/jobs/${jobId}/job_attachments`, params);
  deleteJobAttachment = (jobId: number, attachmentId: number) =>
    this.delete(`/api/v4/jobs/${jobId}/job_attachments/${attachmentId}`, {});
  getNotes = (jobId: number) => this.get(`/api/v4/jobs/${jobId}/job_notes`, {});
  getTask = () => this.get(`/api/v4/tasks/assignable_users`, {});
  getMoveJobs = (jobId: number) => this.get(`/api/v4/jobs/${jobId}/target_entities`, {});
  moveJob = (jobId: number, entityId: string) =>
    this.post(`/api/v4/jobs/${jobId}/move?entity_id=${entityId}`, {});
  newNote = (jobId: number, params: { job_note: { body: string } }) =>
    this.post(`/api/v4/jobs/${jobId}/job_notes`, params);
  newTask = (
    params: { task: ITaskType },
    query?: { job_id: number } | { application_id: number }
  ) => this.post(`/api/v4/tasks?${query && stringify(query)}`, params);
  putNote = (jobId: number, noteId: number, params: { job_note: { body: string } }) =>
    this.put(`/api/v4/jobs/${jobId}/job_notes/${noteId}`, params);
  deleteNote = (jobId: number, noteId: number) =>
    this.delete(`/api/v4/jobs/${jobId}/job_notes/${noteId}`, {});
  getEmailStatuses = (jobId: number, params) =>
    this.getPaginated(`/api/v4/jobs/${jobId}/job_email_statuses`, params);
  cancelEmailBatch = (jobId: number, emailStatusId: number) =>
    this.post(`/api/v4/jobs/${jobId}/job_email_statuses/${emailStatusId}/cancel`);
  getApplications = (jobId: number, params) =>
    this.getPaginated(`/api/v4/jobs/${jobId}/applications`, params);
  getAllApplications = (jobId: number, params) =>
    this.getPaginated(`/api/v4/jobs/${jobId}/applications`, params);
  deleteApplications = (url: string, params: { application_ids: number[] }) =>
    this.delete(url, params);
  getApplicationStatuses = (jobId: number, params) =>
    this.getPaginated(`/api/v4/jobs/${jobId}/job_application_statuses`, params);
  getApplicationLocations = (jobId: number) =>
    this.get(`/api/v4/jobs/${jobId}/applications_locations`, {});
  getFilterableStatuses = (jobId: number, params) =>
    this.getPaginated(`/api/v4/jobs/${jobId}/job_application_statuses/filterable_statuses`, params);
  getFilterableCountries = (jobId: number, params) =>
    this.getPaginated(`/api/v4/jobs/${jobId}/applications/countries`, params);
  getFilterableSuburbs = (jobId: number, params) =>
    this.getPaginated(`/api/v4/jobs/${jobId}/applications/suburbs`, params);
  getWorkflows = (params, jobId: number) =>
    this.getPaginated(`/api/v4/workflows?job_id=${jobId}`, params);
  assignWorkflow = (params: { workflow: { body: IWorkflow } }, jobId: number) =>
    this.post(`/api/v4/workflows?job_id=${jobId}`, params);
  getRevision = (jobId: number, params) =>
    this.getPaginated(`/api/v4/jobs/${jobId}/job_revisions`, params);
  revertRevision = (jobId: number, id: number) =>
    this.post(`/api/v4/jobs/${jobId}/job_revisions/${id}/revert`, {});
  putUnarchiveJob = (params: {}) => this.put(`/api/v4/jobs/unarchive`, params);
  putApplicationStatus = (jobId: number, applicationId: number, statusId: number) =>
    this.put(
      `/api/v4/jobs/${jobId}/applications/${applicationId}/application_status/${statusId}`,
      {}
    );
  newField = (jobId: number, params: { field: { body: string } }) =>
    this.post(`/api/v4/fields?job_id=${jobId}`, params);
  addSystemField = (jobId: number, systemFieldId: number) =>
    this.post(`/api/v4/system_fields?job_id=${jobId}&system_field_id=${systemFieldId}`, {});
  deleteQuestion = (id: number, jobId: number) =>
    this.delete(`/api/v4/fields/${id}?job_id=${jobId}`, {});
  cloneQuestion = (id: number, jobId: number) =>
    this.post(`/api/v4/fields/${id}/clone?job_id=${jobId}`, {});
  putQuestion = (id: number, jobId: number, params: { field: { body: string } }) =>
    this.put(`/api/v4/fields/${id}?job_id=${jobId}`, params);
  newHRField = (applicationQuestionTemplateId: number, params: { field: { body: string } }) =>
    this.post(
      `/api/v4/fields?application_question_template_id=${applicationQuestionTemplateId}`,
      params
    );
  putHRQuestion = (
    id: number,
    applicationQuestionTemplateId: number,
    params: { field: { body: string } }
  ) =>
    this.put(
      `/api/v4/fields/${id}?application_question_template_id=${applicationQuestionTemplateId}`,
      params
    );
  addHRQuestionSystemField = (systemFieldId: number, applicationQuestionTemplateId: number) =>
    this.post(
      `/api/v4/system_fields?system_field_id=${systemFieldId}&application_question_template_id=${applicationQuestionTemplateId}`,
      {}
    );
  deleteHRQuestion = (id: number, applicationQuestionTemplateId: number) =>
    this.delete(
      `/api/v4/fields/${id}?application_question_template_id=${applicationQuestionTemplateId}`,
      {}
    );
  referenceValidation = (params) => this.get(`/api/v4/jobs/validating_reference`, params);
  getJobTimezones = () => this.get(`/api/v4/timezones`, {});
  getJobBrand = (jobId: number) => this.get(`/api/v4/jobs/${jobId}/brands`, {});
  getJobAutoRaters = (jobId: number) => this.get(`/api/v4/jobs/${jobId}/auto_raters`, {});
  getJobPanelChairs = (jobId: number) => this.get(`/api/v4/panel_chairs?job_id=${jobId}`, {});
  getJobrequisitions = (params: { override_limit: boolean; limit: number }) =>
    this.get(`/api/v4/requisitions`, params);
  getJobClassifications = () => this.get(`/api/v4/job_classifications`, {});
  getJobAssignableUsers = (jobId: number) =>
    this.get(`/api/v4/assignable_users?job_id=${jobId}`, {});
  getSalesPeople = () => this.get(`/api/v4/sales_people`, {});
  getRMteamMembers = () => this.get(`/api/v4/rm_team_members`, {});
  getJobLocales = () => this.get(`/api/v4/locales`, {});
  updateJob = (jobId: number, params: { job: { job: { body: string } } }) =>
    this.put(`/api/v4/jobs/${jobId}`, params);
  updateJobTemplate = (jobTemplateId: number, params: any) =>
    this.put(`/api/v4/job_templates/${jobTemplateId}`, params);
  getEvents = (date: Date | string, typeId?: number) =>
    this.get('/api/v4/calendar/events', {
      date: date,
      event_type_id: typeId || undefined,
      override_limit: true
    });
  getJobEmailTemplates = (jobId: number): Promise<{ res: IEmailTemplateList }> =>
    RecruitApiClient.get(`/api/v4/jobs/${jobId}/email_templates`, { group_templates: true });
  getEmailTemplates = (type?: string) =>
    this.get(`/api/v4/calendar/email_templates/grouped_email_templates`, { type });
  getEmailTemplate = (templateId: number) =>
    this.get(`/api/v4/calendar/email_templates/${templateId}`, {});
  getRecruiters = () => this.get(`/api/v4/calendar/users`, {});
  getEventJobs = () => this.get(`/api/v4/calendar/jobs`, {});
  getPlaceholders = (type: string) =>
    this.get(`/api/v4/calendar/email_templates/placeholders`, { type: type || '' });
  addToEventSms = (
    jobId: number,
    eventId: number,
    applicationIds: number[],
    content: string,
    smsTemplateId?: number
  ) =>
    this.post(`/api/v4/jobs/${jobId}/applications/bulk_sms/add_to_event`, {
      event_id: eventId,
      applications_ids: applicationIds,
      sms_template_id: smsTemplateId,
      content: content,
      added: true
    });
  sendInviteSms = (
    jobId: number,
    eventTypeId: number,
    applicationIds: number[],
    content: string,
    smsTemplateId?: number
  ) =>
    this.post(`/api/v4/jobs/${jobId}/applications/bulk_sms/invite_to_timeslot`, {
      event_type_id: eventTypeId,
      applications_ids: applicationIds,
      content: content,
      sms_template_id: smsTemplateId,
      invited: true
    });
  sendEmail = (
    jobId: number,
    eventId: number,
    emailTemplateId: number,
    content: string,
    applicationIds: number[]
  ) =>
    this.post(`/api/v4/jobs/${jobId}/applications/email`, {
      email: {
        event_id: eventId,
        email_template_id: emailTemplateId,
        content: content,
        application_ids: applicationIds
      }
    });
  updateNote = (jobId: number, applicationId: number, params: { suitability_comment: string }) =>
    this.put(`/api/v4/jobs/${jobId}/applications/${applicationId}/update_notepad`, params);
  addRecord = (jobId: number, applicationId: number, params: { subject: string; body: string }) =>
    this.post(`/api/v4/jobs/${jobId}/applications/${applicationId}/permanent_record`, {
      note: params
    });
  getJobTemplates = (): Promise<{ job_templates: IJobTemplate[] }> =>
    this.get(`/api/v4/job_templates`, { override_limit: true, limit: 100 });
  importTemplate = (jobTemplateId: number, targetJobId: number) =>
    this.post(`/api/v4/job_templates/${jobTemplateId}/import_template`, {
      target_job_id: targetJobId
    });
}

export default new Api();
